'use client';

/**
 * Currency Converter Content
 *
 * Client component that displays exchange rates for the Mauritian Rupee (MUR)
 * against all currencies returned by the API, plus rich SEO content about
 * currency considerations when buying property in Mauritius.
 */

import { useCurrency } from '@/contexts/CurrencyContext';
import { CURRENCY_CONFIG, type Currency } from '@/lib/config/app.config';
import Link from 'next/link';
import { useCallback, useMemo, useState } from 'react';

interface CurrencyConverterContentProps {
  locale: string;
  basePath: string;
}

const FLAG_EMOJI: Record<string, string> = {
  AED: '🇦🇪',
  AFN: '🇦🇫',
  ALL: '🇦🇱',
  AMD: '🇦🇲',
  ANG: '🇨🇼',
  AOA: '🇦🇴',
  ARS: '🇦🇷',
  AUD: '🇦🇺',
  AWG: '🇦🇼',
  AZN: '🇦🇿',
  BAM: '🇧🇦',
  BBD: '🇧🇧',
  BDT: '🇧🇩',
  BGN: '🇧🇬',
  BHD: '🇧🇭',
  BIF: '🇧🇮',
  BMD: '🇧🇲',
  BND: '🇧🇳',
  BOB: '🇧🇴',
  BRL: '🇧🇷',
  BSD: '🇧🇸',
  BTN: '🇧🇹',
  BWP: '🇧🇼',
  BYN: '🇧🇾',
  BZD: '🇧🇿',
  CAD: '🇨🇦',
  CDF: '🇨🇩',
  CHF: '🇨🇭',
  CLP: '🇨🇱',
  CNY: '🇨🇳',
  COP: '🇨🇴',
  CRC: '🇨🇷',
  CUP: '🇨🇺',
  CVE: '🇨🇻',
  CZK: '🇨🇿',
  DJF: '🇩🇯',
  DKK: '🇩🇰',
  DOP: '🇩🇴',
  DZD: '🇩🇿',
  EGP: '🇪🇬',
  ERN: '🇪🇷',
  ETB: '🇪🇹',
  EUR: '🇪🇺',
  FJD: '🇫🇯',
  FKP: '🇫🇰',
  GBP: '🇬🇧',
  GEL: '🇬🇪',
  GHS: '🇬🇭',
  GIP: '🇬🇮',
  GMD: '🇬🇲',
  GNF: '🇬🇳',
  GTQ: '🇬🇹',
  GYD: '🇬🇾',
  HKD: '🇭🇰',
  HNL: '🇭🇳',
  HRK: '🇭🇷',
  HTG: '🇭🇹',
  HUF: '🇭🇺',
  IDR: '🇮🇩',
  ILS: '🇮🇱',
  INR: '🇮🇳',
  IQD: '🇮🇶',
  IRR: '🇮🇷',
  ISK: '🇮🇸',
  JMD: '🇯🇲',
  JOD: '🇯🇴',
  JPY: '🇯🇵',
  KES: '🇰🇪',
  KGS: '🇰🇬',
  KHR: '🇰🇭',
  KMF: '🇰🇲',
  KPW: '🇰🇵',
  KRW: '🇰🇷',
  KWD: '🇰🇼',
  KYD: '🇰🇾',
  KZT: '🇰🇿',
  LAK: '🇱🇦',
  LBP: '🇱🇧',
  LKR: '🇱🇰',
  LRD: '🇱🇷',
  LSL: '🇱🇸',
  LYD: '🇱🇾',
  MAD: '🇲🇦',
  MDL: '🇲🇩',
  MGA: '🇲🇬',
  MKD: '🇲🇰',
  MMK: '🇲🇲',
  MNT: '🇲🇳',
  MOP: '🇲🇴',
  MRU: '🇲🇷',
  MUR: '🇲🇺',
  MVR: '🇲🇻',
  MWK: '🇲🇼',
  MXN: '🇲🇽',
  MYR: '🇲🇾',
  MZN: '🇲🇿',
  NAD: '🇳🇦',
  NGN: '🇳🇬',
  NIO: '🇳🇮',
  NOK: '🇳🇴',
  NPR: '🇳🇵',
  NZD: '🇳🇿',
  OMR: '🇴🇲',
  PAB: '🇵🇦',
  PEN: '🇵🇪',
  PGK: '🇵🇬',
  PHP: '🇵🇭',
  PKR: '🇵🇰',
  PLN: '🇵🇱',
  PYG: '🇵🇾',
  QAR: '🇶🇦',
  RON: '🇷🇴',
  RSD: '🇷🇸',
  RUB: '🇷🇺',
  RWF: '🇷🇼',
  SAR: '🇸🇦',
  SBD: '🇸🇧',
  SCR: '🇸🇨',
  SDG: '🇸🇩',
  SEK: '🇸🇪',
  SGD: '🇸🇬',
  SHP: '🇸🇭',
  SLE: '🇸🇱',
  SLL: '🇸🇱',
  SOS: '🇸🇴',
  SRD: '🇸🇷',
  SSP: '🇸🇸',
  STN: '🇸🇹',
  SYP: '🇸🇾',
  SZL: '🇸🇿',
  THB: '🇹🇭',
  TJS: '🇹🇯',
  TMT: '🇹🇲',
  TND: '🇹🇳',
  TOP: '🇹🇴',
  TRY: '🇹🇷',
  TTD: '🇹🇹',
  TWD: '🇹🇼',
  TZS: '🇹🇿',
  UAH: '🇺🇦',
  UGX: '🇺🇬',
  USD: '🇺🇸',
  UYU: '🇺🇾',
  UZS: '🇺🇿',
  VES: '🇻🇪',
  VND: '🇻🇳',
  VUV: '🇻🇺',
  WST: '🇼🇸',
  XAF: '🇨🇲',
  XCD: '🇦🇬',
  XOF: '🇸🇳',
  XPF: '🇵🇫',
  YER: '🇾🇪',
  ZAR: '🇿🇦',
  ZMW: '🇿🇲',
  ZWL: '🇿🇼',
};

const CURRENCY_FULL_NAMES: Record<string, string> = {
  AED: 'United Arab Emirates Dirham',
  AFN: 'Afghan Afghani',
  ALL: 'Albanian Lek',
  AMD: 'Armenian Dram',
  ANG: 'Netherlands Antillean Guilder',
  AOA: 'Angolan Kwanza',
  ARS: 'Argentine Peso',
  AUD: 'Australian Dollar',
  AWG: 'Aruban Florin',
  AZN: 'Azerbaijani Manat',
  BAM: 'Bosnia-Herzegovina Convertible Mark',
  BBD: 'Barbadian Dollar',
  BDT: 'Bangladeshi Taka',
  BGN: 'Bulgarian Lev',
  BHD: 'Bahraini Dinar',
  BIF: 'Burundian Franc',
  BMD: 'Bermudan Dollar',
  BND: 'Brunei Dollar',
  BOB: 'Bolivian Boliviano',
  BRL: 'Brazilian Real',
  BSD: 'Bahamian Dollar',
  BTN: 'Bhutanese Ngultrum',
  BWP: 'Botswanan Pula',
  BYN: 'Belarusian Ruble',
  BZD: 'Belize Dollar',
  CAD: 'Canadian Dollar',
  CDF: 'Congolese Franc',
  CHF: 'Swiss Franc',
  CLP: 'Chilean Peso',
  CNY: 'Chinese Yuan',
  COP: 'Colombian Peso',
  CRC: 'Costa Rican Colón',
  CUP: 'Cuban Peso',
  CVE: 'Cape Verdean Escudo',
  CZK: 'Czech Koruna',
  DJF: 'Djiboutian Franc',
  DKK: 'Danish Krone',
  DOP: 'Dominican Peso',
  DZD: 'Algerian Dinar',
  EGP: 'Egyptian Pound',
  ERN: 'Eritrean Nakfa',
  ETB: 'Ethiopian Birr',
  EUR: 'Euro',
  FJD: 'Fijian Dollar',
  FKP: 'Falkland Islands Pound',
  GBP: 'British Pound Sterling',
  GEL: 'Georgian Lari',
  GHS: 'Ghanaian Cedi',
  GIP: 'Gibraltar Pound',
  GMD: 'Gambian Dalasi',
  GNF: 'Guinean Franc',
  GTQ: 'Guatemalan Quetzal',
  GYD: 'Guyanaese Dollar',
  HKD: 'Hong Kong Dollar',
  HNL: 'Honduran Lempira',
  HRK: 'Croatian Kuna',
  HTG: 'Haitian Gourde',
  HUF: 'Hungarian Forint',
  IDR: 'Indonesian Rupiah',
  ILS: 'Israeli New Shekel',
  INR: 'Indian Rupee',
  IQD: 'Iraqi Dinar',
  IRR: 'Iranian Rial',
  ISK: 'Icelandic Króna',
  JMD: 'Jamaican Dollar',
  JOD: 'Jordanian Dinar',
  JPY: 'Japanese Yen',
  KES: 'Kenyan Shilling',
  KGS: 'Kyrgystani Som',
  KHR: 'Cambodian Riel',
  KMF: 'Comorian Franc',
  KPW: 'North Korean Won',
  KRW: 'South Korean Won',
  KWD: 'Kuwaiti Dinar',
  KYD: 'Cayman Islands Dollar',
  KZT: 'Kazakhstani Tenge',
  LAK: 'Laotian Kip',
  LBP: 'Lebanese Pound',
  LKR: 'Sri Lankan Rupee',
  LRD: 'Liberian Dollar',
  LSL: 'Lesotho Loti',
  LYD: 'Libyan Dinar',
  MAD: 'Moroccan Dirham',
  MDL: 'Moldovan Leu',
  MGA: 'Malagasy Ariary',
  MKD: 'Macedonian Denar',
  MMK: 'Myanma Kyat',
  MNT: 'Mongolian Tugrik',
  MOP: 'Macanese Pataca',
  MRU: 'Mauritanian Ouguiya',
  MUR: 'Mauritian Rupee',
  MVR: 'Maldivian Rufiyaa',
  MWK: 'Malawian Kwacha',
  MXN: 'Mexican Peso',
  MYR: 'Malaysian Ringgit',
  MZN: 'Mozambican Metical',
  NAD: 'Namibian Dollar',
  NGN: 'Nigerian Naira',
  NIO: 'Nicaraguan Córdoba',
  NOK: 'Norwegian Krone',
  NPR: 'Nepalese Rupee',
  NZD: 'New Zealand Dollar',
  OMR: 'Omani Rial',
  PAB: 'Panamanian Balboa',
  PEN: 'Peruvian Sol',
  PGK: 'Papua New Guinean Kina',
  PHP: 'Philippine Peso',
  PKR: 'Pakistani Rupee',
  PLN: 'Polish Zloty',
  PYG: 'Paraguayan Guarani',
  QAR: 'Qatari Rial',
  RON: 'Romanian Leu',
  RSD: 'Serbian Dinar',
  RUB: 'Russian Ruble',
  RWF: 'Rwandan Franc',
  SAR: 'Saudi Riyal',
  SBD: 'Solomon Islands Dollar',
  SCR: 'Seychellois Rupee',
  SDG: 'Sudanese Pound',
  SEK: 'Swedish Krona',
  SGD: 'Singapore Dollar',
  SHP: 'Saint Helena Pound',
  SLE: 'Sierra Leonean Leone',
  SLL: 'Sierra Leonean Leone',
  SOS: 'Somali Shilling',
  SRD: 'Surinamese Dollar',
  SSP: 'South Sudanese Pound',
  STN: 'São Tomé and Príncipe Dobra',
  SYP: 'Syrian Pound',
  SZL: 'Swazi Lilangeni',
  THB: 'Thai Baht',
  TJS: 'Tajikistani Somoni',
  TMT: 'Turkmenistani Manat',
  TND: 'Tunisian Dinar',
  TOP: 'Tongan Paʻanga',
  TRY: 'Turkish Lira',
  TTD: 'Trinidad and Tobago Dollar',
  TWD: 'New Taiwan Dollar',
  TZS: 'Tanzanian Shilling',
  UAH: 'Ukrainian Hryvnia',
  UGX: 'Ugandan Shilling',
  USD: 'United States Dollar',
  UYU: 'Uruguayan Peso',
  UZS: 'Uzbekistan Som',
  VES: 'Venezuelan Bolívar',
  VND: 'Vietnamese Dong',
  VUV: 'Vanuatu Vatu',
  WST: 'Samoan Tala',
  XAF: 'CFA Franc BEAC',
  XCD: 'East Caribbean Dollar',
  XOF: 'CFA Franc BCEAO',
  XPF: 'CFP Franc',
  YER: 'Yemeni Rial',
  ZAR: 'South African Rand',
  ZMW: 'Zambian Kwacha',
  ZWL: 'Zimbabwean Dollar',
};

function formatRate(rate: number, decimals = 4): string {
  return rate.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatAmount(amount: number, currency: string): string {
  const config = CURRENCY_CONFIG[currency as Currency];
  if (!config) {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function CurrencyConverterContent({ locale: _locale, basePath }: CurrencyConverterContentProps) {
  const { rates, isLoading, ratesLastUpdated, convertBetween } = useCurrency();
  const [amount, setAmount] = useState<number>(1000000);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');

  const otherCurrencies = useMemo(() => {
    if (!rates) return [];
    return Object.keys(rates).filter((c) => c !== 'MUR');
  }, [rates]);

  const convertedAmount = useMemo(() => {
    if (!convertBetween) return null;
    return convertBetween(amount, 'MUR', selectedCurrency);
  }, [amount, selectedCurrency, convertBetween]);

  const murRate = useMemo(() => {
    if (!rates) return null;
    const murUsd = rates['MUR'];
    const targetUsd = selectedCurrency === 'USD' ? 1 : rates[selectedCurrency];
    if (!murUsd || !targetUsd) return null;
    return targetUsd / murUsd;
  }, [rates, selectedCurrency]);

  const inverseMurRate = useMemo(() => {
    if (!murRate) return null;
    return 1 / murRate;
  }, [murRate]);

  const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    const num = Number.parseFloat(val);
    setAmount(Number.isNaN(num) ? 0 : num);
  }, []);

  const formattedDate = useMemo(() => {
    if (!ratesLastUpdated) return null;
    try {
      return new Date(ratesLastUpdated).toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return ratesLastUpdated;
    }
  }, [ratesLastUpdated]);

  return (
    <div className="bg-linear-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-linear-to-br from-(--color-primary) via-(--color-primary)/90 to-(--color-accent) text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-white/70 mb-4 font-inter">
              Mauritius Currency Guide
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Mauritian Rupee
              <span className="block text-white/80 mt-2">Exchange Rates</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl font-inter">
              Check the latest Mauritian Rupee (MUR) exchange rates against major world currencies.
              Whether you&apos;re planning a property purchase or simply visiting Mauritius, stay
              informed with our daily-updated rates.
            </p>
          </div>
        </div>
      </section>

      {/* Converter Tool */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10 mb-16">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row lg:items-end gap-8">
            {/* Amount Input */}
            <div className="flex-1">
              <label
                htmlFor="amount"
                className="block text-sm font-semibold text-gray-700 mb-2 font-inter"
              >
                Amount in Mauritian Rupees (MUR)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-lg font-inter">
                  Rs
                </span>
                <input
                  id="amount"
                  type="text"
                  inputMode="decimal"
                  value={amount.toLocaleString('en-US')}
                  onChange={handleAmountChange}
                  className="w-full pl-12 pr-4 py-4 text-xl font-semibold border-2 border-gray-200 rounded-xl focus:border-(--color-primary) focus:ring-2 focus:ring-(--color-primary)/20 outline-none transition-all font-inter"
                  aria-label="Amount in Mauritian Rupees"
                />
              </div>
            </div>

            {/* Arrow */}
            <div className="hidden lg:flex items-center justify-center pb-2">
              <svg className="w-8 h-8 text-(--color-primary)" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </div>

            {/* Currency Selector */}
            <div className="flex-1">
              <label
                htmlFor="currency"
                className="block text-sm font-semibold text-gray-700 mb-2 font-inter"
              >
                Convert to
              </label>
              <select
                id="currency"
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="w-full px-4 py-4 text-xl font-semibold border-2 border-gray-200 rounded-xl focus:border-(--color-primary) focus:ring-2 focus:ring-(--color-primary)/20 outline-none transition-all appearance-none bg-white font-inter cursor-pointer"
                aria-label="Target currency"
              >
                {otherCurrencies.map((code) => (
                  <option key={code} value={code}>
                    {FLAG_EMOJI[code] || ''} {code} — {CURRENCY_FULL_NAMES[code] || code}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Result */}
          {isLoading ? (
            <div className="mt-8 pt-8 border-t border-gray-100 text-center">
              <div className="inline-flex items-center gap-3 text-gray-500 font-inter">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading exchange rates...
              </div>
            </div>
          ) : convertedAmount !== null && murRate ? (
            <div className="mt-8 pt-8 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4">
                <p className="text-3xl sm:text-4xl font-bold text-(--color-text) font-inter">
                  {formatAmount(convertedAmount, selectedCurrency)}
                </p>
                <p className="text-gray-500 font-inter">
                  = {formatAmount(amount, 'MUR')}
                </p>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500 font-inter">
                <span>
                  1 MUR = {formatRate(murRate)} {selectedCurrency}
                </span>
                <span>
                  1 {selectedCurrency} = {formatRate(inverseMurRate!)} MUR
                </span>
              </div>
            </div>
          ) : (
            <div className="mt-8 pt-8 border-t border-gray-100 text-center text-gray-500 font-inter">
              Exchange rates are currently unavailable. Please try again shortly.
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-6 flex items-start gap-2 text-xs text-gray-400 font-inter">
            <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
            <p>
              Rates are updated daily and are indicative only. They should not be used for
              financial transactions. For live rates, please consult your bank or a licensed
              foreign exchange provider.
              {formattedDate && (
                <span className="block mt-1">Last updated: {formattedDate}</span>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* All Rates Table */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-(--color-text) mb-2">
          MUR Exchange Rate Table
        </h2>
        <p className="text-gray-500 mb-8 font-inter">
          How much is 1 Mauritian Rupee worth in other currencies
        </p>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
                <div className="h-8 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : rates ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherCurrencies.map((code) => {
              const murUsd = rates['MUR'];
              const targetUsd = code === 'USD' ? 1 : rates[code];
              if (!murUsd || !targetUsd) return null;
              const rate = targetUsd / murUsd;
              const inverse = murUsd / targetUsd;

              return (
                <div
                  key={code}
                  className="bg-white rounded-xl border border-gray-100 hover:border-(--color-primary)/30 hover:shadow-lg p-6 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl" role="img" aria-label={`${code} flag`}>
                      {FLAG_EMOJI[code] || '💱'}
                    </span>
                    <div>
                      <p className="font-bold text-(--color-text) font-inter">{code}</p>
                      <p className="text-xs text-gray-400 font-inter">
                        {CURRENCY_FULL_NAMES[code] || code}
                      </p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-(--color-text) font-inter group-hover:text-(--color-primary) transition-colors">
                    {formatRate(rate)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 font-inter">
                    1 {code} = {formatRate(inverse, 2)} MUR
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 font-inter">
            Exchange rates are currently unavailable.
          </p>
        )}
      </section>

      {/* SEO Content Sections */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-4xl mx-auto">
          {/* Understanding MUR */}
          <article className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-(--color-text) mb-6">
              Understanding the Mauritian Rupee (MUR)
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 font-inter space-y-4">
              <p>
                The Mauritian Rupee (MUR) is the official currency of the Republic of Mauritius,
                a stunning island nation in the Indian Ocean. Issued by the Bank of Mauritius,
                the rupee has been the country&apos;s legal tender since 1877, replacing the
                earlier Indian rupee and the colonial-era dollar.
              </p>
              <p>
                One Mauritian Rupee is subdivided into 100 cents. Banknotes are available in
                denominations of Rs 25, 50, 100, 200, 500, 1,000 and 2,000, while coins come
                in 5c, 20c, Rs 1, 5, 10 and 20 values. The currency code under ISO 4217 is
                <strong> MUR</strong> and the commonly used symbol is <strong>Rs</strong>.
              </p>
              <p>
                Mauritius operates a managed float exchange rate regime, meaning the Bank of
                Mauritius occasionally intervenes in the foreign exchange market to smooth out
                excessive volatility while allowing market forces to determine the
                rupee&apos;s value over time.
              </p>
            </div>
          </article>

          {/* Currency for Property Buyers */}
          <article className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-(--color-text) mb-6">
              Currency Considerations for Mauritius Property Buyers
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 font-inter space-y-4">
              <p>
                If you are considering purchasing property in Mauritius, understanding currency
                exchange rates is essential. Property prices in Mauritius are typically quoted in
                Mauritian Rupees (MUR), though some luxury developments and IRS/RES/PDS scheme
                properties may also be listed in US Dollars (USD) or Euros (EUR) for the
                convenience of international buyers.
              </p>
              <p>
                Foreign nationals are permitted to purchase property in Mauritius under several
                government-approved schemes, including the Integrated Resort Scheme (IRS),
                Real Estate Scheme (RES), Property Development Scheme (PDS), and Smart City
                Scheme. A minimum investment threshold applies, and the purchase price must
                generally be paid in a foreign currency through the banking system.
              </p>
              <p>
                Exchange rate fluctuations can have a significant impact on the effective cost
                of your investment. A favourable movement of just a few percentage points on a
                multi-million-rupee transaction can translate into substantial savings. We
                recommend consulting with a specialist foreign exchange broker who can offer
                forward contracts to lock in a rate, protecting you from adverse movements
                between the time you agree a price and the date of completion.
              </p>
            </div>
          </article>

          {/* Key Facts */}
          <article className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-(--color-text) mb-6">
              Key Facts About the Mauritian Rupee
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { label: 'Currency Name', value: 'Mauritian Rupee' },
                { label: 'ISO 4217 Code', value: 'MUR' },
                { label: 'Symbol', value: 'Rs (₨)' },
                { label: 'Subunit', value: '1 Rupee = 100 Cents' },
                { label: 'Central Bank', value: 'Bank of Mauritius' },
                { label: 'Exchange Regime', value: 'Managed Float' },
                { label: 'Country', value: 'Republic of Mauritius' },
                { label: 'Banknotes', value: 'Rs 25 – Rs 2,000' },
              ].map((fact) => (
                <div
                  key={fact.label}
                  className="bg-gray-50 rounded-xl p-5 border border-gray-100"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1 font-inter">
                    {fact.label}
                  </p>
                  <p className="text-lg font-bold text-(--color-text) font-inter">
                    {fact.value}
                  </p>
                </div>
              ))}
            </div>
          </article>

          {/* Tips for Exchanging Currency */}
          <article className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-(--color-text) mb-6">
              Tips for Exchanging Currency in Mauritius
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 font-inter space-y-4">
              <p>
                Whether you are visiting Mauritius on holiday or relocating permanently, here
                are some practical tips for managing your currency exchange:
              </p>
              <ul className="space-y-3 list-none pl-0">
                {[
                  {
                    title: 'Use local banks or licensed bureaux de change',
                    desc: 'You will generally receive better rates at banks and licensed money changers than at hotels or the airport. Major banks such as MCB, SBM, and Barclays Mauritius offer competitive exchange services.',
                  },
                  {
                    title: 'Consider a multi-currency account',
                    desc: 'If you are making regular transfers to Mauritius, a multi-currency bank account can help you hold funds in MUR and convert at opportune moments.',
                  },
                  {
                    title: 'Be aware of transfer fees',
                    desc: 'International wire transfers often carry fees from both the sending and receiving banks. Specialist FX providers such as Wise (formerly TransferWise) or OFX may offer lower fees and better rates than traditional banks.',
                  },
                  {
                    title: 'Plan large transfers strategically',
                    desc: 'For property purchases, consider using forward contracts or limit orders through an FX broker. These tools allow you to set a target rate and execute the transfer automatically when the market reaches your desired level.',
                  },
                  {
                    title: 'Keep receipts for repatriation',
                    desc: 'If you sell a property in Mauritius and wish to repatriate the proceeds, you will need to demonstrate that the original funds were brought into the country through the banking system. Retain all exchange receipts and bank statements.',
                  },
                ].map((tip) => (
                  <li key={tip.title} className="bg-white rounded-xl border border-gray-100 p-6">
                    <p className="font-bold text-(--color-text) mb-2 font-inter">
                      {tip.title}
                    </p>
                    <p className="text-gray-600 font-inter">{tip.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </article>

          {/* Economic Context */}
          <article className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-(--color-text) mb-6">
              The Mauritius Economy and Currency Outlook
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 font-inter space-y-4">
              <p>
                Mauritius has one of the most stable and diversified economies in Africa. Once
                heavily reliant on sugar exports, the country has successfully transitioned into
                a services-based economy with strong pillars in financial services, tourism,
                information technology, and real estate.
              </p>
              <p>
                The country consistently ranks highly in the Ibrahim Index of African Governance
                and the World Bank&apos;s Ease of Doing Business rankings. This economic
                stability, combined with favourable tax treaties with numerous countries, has
                made Mauritius an attractive destination for international investors.
              </p>
              <p>
                The real estate sector has been a significant driver of foreign direct investment,
                particularly through the PDS and Smart City schemes. These government-backed
                programmes offer non-citizens the right to purchase property and, in many cases,
                obtain a residence permit — making currency planning an integral part of the
                investment process.
              </p>
            </div>
          </article>

          {/* CTA */}
          <div className="bg-linear-to-br from-(--color-primary) to-(--color-accent) rounded-2xl p-8 lg:p-12 text-white text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Ready to Invest in Mauritius Property?
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto font-inter">
              Our team at Allys Real Estate can guide you through every step of the property
              purchase process in Mauritius, including currency and payment considerations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href={`${basePath}/properties`}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-(--color-primary) font-bold rounded-xl hover:bg-gray-50 transition-colors font-inter"
              >
                Browse Properties
              </Link>
              <Link
                href={`${basePath}/contact`}
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold rounded-xl hover:bg-white/10 transition-colors font-inter"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Mauritian Rupee (MUR) Exchange Rates - Currency Converter',
            description:
              'Check the latest Mauritian Rupee (MUR) exchange rates against major world currencies. Updated daily.',
            mainEntity: {
              '@type': 'Table',
              about: {
                '@type': 'Thing',
                name: 'Mauritian Rupee exchange rates',
              },
            },
            isPartOf: {
              '@type': 'WebSite',
              name: 'Allys Real Estate',
              url: 'https://www.allys.mu',
            },
          }),
        }}
      />
    </div>
  );
}
